import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { Toaster } from '@/components/ui/sonner';
import { AuthProvider } from '@/contexts/AuthContext';
import { ThemeProvider } from '@/contexts/ThemeContext';
import { CallProvider } from '@/components/call';
import '@/App.css';

// Public Pages
import LandingPage from '@/pages/LandingPage';
import HomePage from '@/pages/public/HomePage';
import AboutPage from '@/pages/public/AboutPage';
import ServicesPage from '@/pages/public/ServicesPage';
import NRIPage from '@/pages/public/NRIPage';
import ToolsPage from '@/pages/public/ToolsPage';
import ResourcesPage from '@/pages/public/ResourcesPage';
import PricingPage from '@/pages/public/PricingPage';
import ContactPage from '@/pages/public/ContactPage';
import FAQPage from '@/pages/public/FAQPage';
import PrivacyPolicy from '@/pages/public/PrivacyPolicy';
import TermsConditions from '@/pages/public/TermsConditions';
import RefundPolicy from '@/pages/public/RefundPolicy';
import DebugLogin from '@/pages/DebugLogin';

// Auth Pages
import LoginPage from '@/pages/auth/LoginPage';
import { ProtectedRoute } from '@/components/auth/ProtectedRoute';
import { AuthCallback } from '@/components/auth/AuthCallback';

// Client Pages
import ClientDashboard from '@/pages/client/ClientDashboard';
import TaxFilingWizard from '@/pages/client/TaxFilingWizard';
import FilingStatusPage from '@/pages/client/FilingStatusPage';
import ProfilePage from '@/pages/client/ProfilePage';
import ReferralsPage from '@/pages/client/ReferralsPage';
import PaymentsPage from '@/pages/client/PaymentsPage';

// Payment Pages
import PaymentSuccess from '@/pages/payment/PaymentSuccess';
import PaymentCancel from '@/pages/payment/PaymentCancel';

// Business Pages
import BusinessDashboard from '@/pages/business/BusinessDashboard';
import BusinessFilingWizard from '@/pages/business/BusinessFilingWizard';
import BusinessFilingStatusPage from '@/pages/business/BusinessFilingStatusPage';
import BusinessRequestsPage from '@/pages/business/BusinessRequestsPage';

// LLC Pages
import LLCCreationDashboard from '@/pages/llc/LLCCreationDashboard';
import LLCCreationWizard from '@/pages/llc/LLCCreationWizard';
import MyLLCRequests from '@/pages/llc/MyLLCRequests';
import LLCRequestDetailView from '@/pages/llc/LLCRequestDetailView';
import LLCFilingStatusPage from '@/pages/llc/LLCFilingStatusPage';

// Admin Pages
import AdminDashboard from '@/pages/admin/AdminDashboard';
import AdminConsultations from '@/pages/admin/AdminConsultations';
import UsersManagement from '@/pages/admin/UsersManagement';
import AgentsManagement from '@/pages/admin/AgentsManagement';
import FilingsManagement from '@/pages/admin/FilingsManagement';
import PricingManagement from '@/pages/admin/PricingManagement';
import LLCPricingManagement from '@/pages/admin/LLCPricingManagement';
import AdminPayments from '@/pages/admin/AdminPayments';
import AdminSettings from '@/pages/admin/AdminSettings';
import AdminReferrals from '@/pages/admin/AdminReferrals';
import ComplianceDashboard from '@/pages/admin/ComplianceDashboard';
import FlyerManagement from '@/pages/admin/FlyerManagement';
import FAQManagement from '@/pages/admin/FAQManagement';
import AdminReports from '@/pages/admin/AdminReports';
import AdminFilingDetailView from '@/pages/admin/AdminFilingDetailView';

// Agent Pages
import AgentDashboard from '@/pages/agent/AgentDashboard';
import AgentClients from '@/pages/agent/AgentClients';
import ClientFilingDetailView from '@/pages/agent/ClientFilingDetailView';
import TaxFilingDetailView from '@/pages/agent/TaxFilingDetailView';
import AgentLLCFormations from '@/pages/agent/AgentLLCFormations';
import LLCFormationDetailView from '@/pages/agent/LLCFormationDetailView';
import AgentPayments from '@/pages/agent/AgentPayments';

// Chat Pages
import ChatDashboard from '@/pages/chat/ChatDashboard';

// Agency Pages
import AgencyDashboard from '@/pages/agency/AgencyDashboard';
import ThemeBuilder from '@/pages/agency/ThemeBuilder';

// Legal Pages
import TermsPage from '@/pages/legal/TermsPage';
import PrivacyPage from '@/pages/legal/PrivacyPage';
import RefundPage from '@/pages/legal/RefundPage';
import DisclaimerPage from '@/pages/legal/DisclaimerPage';
import CookiesPage from '@/pages/legal/CookiesPage';

function App() {
  // State to track if we have a session_id that needs processing
  const [processingOAuth, setProcessingOAuth] = React.useState(false);
  
  // Check for session_id in URL hash - reactive to hash changes
  React.useEffect(() => {
    const checkForSessionId = () => {
      const hasSessionId = window.location.hash?.includes('session_id=');
      console.log('App - Checking for session_id:', hasSessionId, window.location.hash);
      setProcessingOAuth(hasSessionId);
    };
    
    // Check on mount
    checkForSessionId();
    
    // Listen for hash changes
    window.addEventListener('hashchange', checkForSessionId);
    
    return () => {
      window.removeEventListener('hashchange', checkForSessionId);
    };
  }, []);

  return (
    <HelmetProvider>
    <ThemeProvider>
      <AuthProvider>
        <CallProvider>
        <BrowserRouter>
          <div className="App">
            {/* Handle OAuth callback before normal routing */}
            {processingOAuth ? (
              <AuthCallback />
            ) : (
            <Routes>
          {/* Public Pages */}
          <Route path="/" element={<HomePage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/services" element={<ServicesPage />} />
          <Route path="/services/*" element={<ServicesPage />} />
          <Route path="/nri" element={<NRIPage />} />
          <Route path="/tools" element={<ToolsPage />} />
          <Route path="/resources" element={<ResourcesPage />} />
          <Route path="/pricing" element={<PricingPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/faq" element={<FAQPage />} />
          
          {/* Legacy Landing Page */}
          <Route path="/landing" element={<LandingPage />} />

          {/* Legal Pages */}
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/refunds" element={<RefundPage />} />
          <Route path="/disclaimer" element={<DisclaimerPage />} />
          <Route path="/cookies" element={<CookiesPage />} />

          {/* Debug Route */}
          <Route path="/debug-login" element={<DebugLogin />} />

          {/* Auth Routes */}
          <Route path="/login" element={<LoginPage />} />

          {/* Client Routes - Protected */}
          <Route path="/dashboard" element={
            <ProtectedRoute allowedRoles={['client']}>
              <ClientDashboard />
            </ProtectedRoute>
          } />
          <Route path="/filing/start" element={
            <ProtectedRoute allowedRoles={['client']}>
              <TaxFilingWizard />
            </ProtectedRoute>
          } />
          <Route path="/filing-status" element={
            <ProtectedRoute allowedRoles={['client']}>
              <FilingStatusPage />
            </ProtectedRoute>
          } />
          <Route path="/payments" element={
            <ProtectedRoute allowedRoles={['client']}>
              <PaymentsPage />
            </ProtectedRoute>
          } />
          <Route path="/profile" element={
            <ProtectedRoute>
              <ProfilePage />
            </ProtectedRoute>
          } />
          <Route path="/referrals" element={
            <ProtectedRoute allowedRoles={['client']}>
              <ReferralsPage />
            </ProtectedRoute>
          } />

          {/* Payment Routes - Protected */}
          <Route path="/payment/success" element={
            <ProtectedRoute>
              <PaymentSuccess />
            </ProtectedRoute>
          } />
          <Route path="/payment/cancel" element={
            <ProtectedRoute>
              <PaymentCancel />
            </ProtectedRoute>
          } />

          {/* Business Routes - Protected */}
          <Route path="/business/dashboard" element={
            <ProtectedRoute allowedRoles={['business', 'client']}>
              <BusinessDashboard />
            </ProtectedRoute>
          } />
          <Route path="/business/filing/start" element={
            <ProtectedRoute allowedRoles={['business', 'client']}>
              <BusinessFilingWizard />
            </ProtectedRoute>
          } />
          <Route path="/business/requests" element={
            <ProtectedRoute allowedRoles={['business', 'client']}>
              <BusinessRequestsPage />
            </ProtectedRoute>
          } />
          <Route path="/business/filing-status" element={
            <ProtectedRoute allowedRoles={['business', 'client']}>
              <BusinessFilingStatusPage />
            </ProtectedRoute>
          } />

          {/* LLC Formation Routes - Protected */}
          <Route path="/llc/dashboard" element={
            <ProtectedRoute allowedRoles={['client', 'business']}>
              <LLCCreationDashboard />
            </ProtectedRoute>
          } />
          <Route path="/llc/create" element={
            <ProtectedRoute allowedRoles={['client', 'business']}>
              <LLCCreationWizard />
            </ProtectedRoute>
          } />
          <Route path="/llc/requests" element={
            <ProtectedRoute allowedRoles={['client', 'business']}>
              <MyLLCRequests />
            </ProtectedRoute>
          } />
          <Route path="/llc/requests/:requestId" element={
            <ProtectedRoute allowedRoles={['client', 'business']}>
              <LLCRequestDetailView />
            </ProtectedRoute>
          } />
          <Route path="/llc/filing-status" element={
            <ProtectedRoute allowedRoles={['client', 'business']}>
              <LLCFilingStatusPage />
            </ProtectedRoute>
          } />

          {/* Admin Routes - Protected */}
          <Route path="/admin/dashboard" element={
            <ProtectedRoute allowedRoles={['admin']}>
              <AdminDashboard />
            </ProtectedRoute>
          } />
          <Route path="/admin/consultations" element={
            <ProtectedRoute allowedRoles={['admin']}>
              <AdminConsultations />
            </ProtectedRoute>
          } />
          <Route path="/admin/users" element={
            <ProtectedRoute allowedRoles={['admin']}>
              <UsersManagement />
            </ProtectedRoute>
          } />
          <Route path="/admin/agents" element={
            <ProtectedRoute allowedRoles={['admin']}>
              <AgentsManagement />
            </ProtectedRoute>
          } />
          <Route path="/admin/filings" element={
            <ProtectedRoute allowedRoles={['admin']}>
              <FilingsManagement />
            </ProtectedRoute>
          } />
          <Route path="/admin/filings/:filingId" element={
            <ProtectedRoute allowedRoles={['admin']}>
              <AdminFilingDetailView />
            </ProtectedRoute>
          } />
          <Route path="/admin/payments" element={
            <ProtectedRoute allowedRoles={['admin']}>
              <AdminPayments />
            </ProtectedRoute>
          } />
          <Route path="/admin/pricing" element={
            <ProtectedRoute allowedRoles={['admin']}>
              <PricingManagement />
            </ProtectedRoute>
          } />
          <Route path="/admin/llc-pricing" element={
            <ProtectedRoute allowedRoles={['admin']}>
              <LLCPricingManagement />
            </ProtectedRoute>
          } />
          <Route path="/admin/chat" element={
            <ProtectedRoute allowedRoles={['admin']}>
              <ChatDashboard />
            </ProtectedRoute>
          } />
          <Route path="/admin/referrals" element={
            <ProtectedRoute allowedRoles={['admin']}>
              <AdminReferrals />
            </ProtectedRoute>
          } />
          <Route path="/admin/settings" element={
            <ProtectedRoute allowedRoles={['admin']}>
              <AdminSettings />
            </ProtectedRoute>
          } />
          <Route path="/admin/compliance" element={
            <ProtectedRoute allowedRoles={['admin']}>
              <ComplianceDashboard />
            </ProtectedRoute>
          } />
          <Route path="/admin/flyer" element={
            <ProtectedRoute allowedRoles={['admin']}>
              <FlyerManagement />
            </ProtectedRoute>
          } />
          <Route path="/admin/faq" element={
            <ProtectedRoute allowedRoles={['admin']}>
              <FAQManagement />
            </ProtectedRoute>
          } />
          <Route path="/admin/reports" element={
            <ProtectedRoute allowedRoles={['admin']}>
              <AdminReports />
            </ProtectedRoute>
          } />
          <Route path="/admin/profile" element={
            <ProtectedRoute allowedRoles={['admin']}>
              <ProfilePage />
            </ProtectedRoute>
          } />

          {/* Agent Routes - Protected */}
          <Route path="/agent/dashboard" element={
            <ProtectedRoute allowedRoles={['agent']}>
              <AgentDashboard />
            </ProtectedRoute>
          } />
          <Route path="/agent/clients" element={
            <ProtectedRoute allowedRoles={['agent']}>
              <AgentClients />
            </ProtectedRoute>
          } />
          <Route path="/agent/clients/:clientId" element={
            <ProtectedRoute allowedRoles={['agent']}>
              <ClientFilingDetailView />
            </ProtectedRoute>
          } />
          <Route path="/agent/filings/:filingId" element={
            <ProtectedRoute allowedRoles={['agent']}>
              <TaxFilingDetailView />
            </ProtectedRoute>
          } />
          <Route path="/agent/payments" element={
            <ProtectedRoute allowedRoles={['agent']}>
              <AgentPayments />
            </ProtectedRoute>
          } />
          <Route path="/agent/llc" element={
            <ProtectedRoute allowedRoles={['agent']}>
              <AgentLLCFormations />
            </ProtectedRoute>
          } />
          <Route path="/agent/llc/:llcId" element={
            <ProtectedRoute allowedRoles={['agent']}>
              <LLCFormationDetailView />
            </ProtectedRoute>
          } />
          <Route path="/agent/chat" element={
            <ProtectedRoute allowedRoles={['agent']}>
              <ChatDashboard />
            </ProtectedRoute>
          } />
          <Route path="/agent/profile" element={
            <ProtectedRoute allowedRoles={['agent']}>
              <ProfilePage />
            </ProtectedRoute>
          } />

          {/* Agency Routes - Protected */}
          <Route path="/agency/dashboard" element={
            <ProtectedRoute allowedRoles={['agency']}>
              <AgencyDashboard />
            </ProtectedRoute>
          } />
          <Route path="/agency/theme" element={
            <ProtectedRoute allowedRoles={['agency']}>
              <ThemeBuilder />
            </ProtectedRoute>
          } />
          <Route path="/agency/users" element={
            <ProtectedRoute allowedRoles={['agency']}>
              <UsersManagement />
            </ProtectedRoute>
          } />
          <Route path="/agency/filings" element={
            <ProtectedRoute allowedRoles={['agency']}>
              <FilingsManagement />
            </ProtectedRoute>
          } />
          <Route path="/agency/filings/:filingId" element={
            <ProtectedRoute allowedRoles={['agency']}>
              <AdminFilingDetailView />
            </ProtectedRoute>
          } />
          <Route path="/agency/chat" element={
            <ProtectedRoute allowedRoles={['agency']}>
              <ChatDashboard />
            </ProtectedRoute>
          } />
          <Route path="/agency/settings" element={
            <ProtectedRoute allowedRoles={['agency']}>
              <ProfilePage />
            </ProtectedRoute>
          } />

          {/* Catch all - redirect to landing */}
          <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
            )}
          <Toaster position="top-right" richColors />
          </div>
        </BrowserRouter>
        </CallProvider>
      </AuthProvider>
    </ThemeProvider>
    </HelmetProvider>
  );
}

export default App;
